// src/components/compliance/KycCaptureWizard.tsx
/**
 * Capture assistant of the self-built GwG identification (Issue #1070).
 *
 * Owner decision 24.09.2026: we do the identification ourselves, no ident
 * provider. The customer photographs the document (front, back, selfie) with
 * the phone camera or the webcam — or uploads photos / a PDF copy. A human
 * releases it afterwards, therefore the wizard ends with «wird geprüft».
 *
 * The API calls come in as a prop: the follow-up ticket («auf dem Handy
 * weitermachen») reuses this wizard against the handoff session instead of the
 * logged-in user.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  Circle,
  FileUp,
  Loader2,
  ScanFace,
  Upload,
} from 'lucide-react'
import CameraCapture, { type CameraUnavailableReason } from './CameraCapture'
import {
  KYC_ANALYSIS_POLL_INTERVAL_MS,
  KYC_ANALYSIS_SKIP_AFTER_MS,
  KYC_ANALYSIS_STATUS,
  KYC_ANALYSIS_TIMEOUT_MS,
  KYC_CAPTURE_MODE,
  KYC_DOCUMENT_TYPE,
  KYC_DOCUMENT_TYPES,
  KYC_IMAGE_QUALITY,
  KYC_LIVENESS_CHALLENGE_KEYS,
  KYC_LIVENESS_FINGER_MAX,
  KYC_LIVENESS_FINGER_MIN,
  KYC_SELF_SLOT,
  KYC_TWO_SIDED_DOCUMENT_TYPES,
  KYC_UPLOAD_ACCEPT,
  KYC_UPLOAD_EXTENSIONS,
  KYC_UPLOAD_MAX_BYTES,
  KYC_UPLOAD_MIME_TYPES,
} from '@/constants/kyc'
import type {
  KycCaptureMode,
  KycDocumentType,
  KycImageQualityValue,
  KycLivenessChallenge,
  KycSelfAnalysis,
  KycSelfApi,
  KycSelfSlot,
  KycSelfStatus,
} from '@/types/kyc'

interface Props {
  /** The three calls of the self identification, already bound to a session. */
  api: KycSelfApi
  /** Random task for the live selfie, as the server picked it. */
  livenessChallenge: string | null
  /** How many fingers to show (1–5) — only sent with `SHOW_FINGERS`. */
  livenessFingerCount?: number | null
  /** The identification is with us — the dialog switches to «wird geprüft». */
  onSubmitted: (status: KycSelfStatus) => void
  /** Back to the choice of method. */
  onBack?: () => void
}

/**
 * Stations of the assistant.
 *
 * `analyzing` sits between the pictures and the form (Issue #1165): our AI
 * reads the document while the customer waits, so the form arrives filled in —
 * and an unusable picture is noticed here instead of by the reviewer.
 */
type Stage = 'setup' | 'capture' | 'analyzing' | 'details'

/** What the analysis holds against one picture, and which one it is. */
interface ImageCritique {
  slot: KycSelfSlot
  reason: KycImageQualityValue
}

/** Error on screen: one sentence, optionally a list (e.g. missing pages). */
interface CaptureError {
  text: string
  items?: string[]
}

/**
 * What we handed to the API for one slot, kept for the summary (Issue #1115).
 *
 * `url` is the object URL of exactly the blob that went up — for a camera
 * capture that is the cropped canvas blob, so the summary shows the stored
 * version and not the raw viewfinder frame.
 */
interface StoredCapture {
  url: string
  mime: string
  name: string
}

const DOCUMENT_TYPE_LABELS: Record<KycDocumentType, string> = {
  ID_CARD: 'Identitätskarte',
  PASSPORT: 'Pass',
  RESIDENCE_PERMIT: 'Ausländerausweis',
  OTHER: 'Anderer amtlicher Ausweis',
}

/**
 * The task as the user is addressed with it. The review page shows the same
 * challenge as a short caption (`kyc.capture.liveness.<KEY>`) — here it is an
 * instruction, so the two registers live side by side.
 *
 * `SHOW_FINGERS` is missing on purpose: its sentence carries the number the
 * server picked and is built in {@link fingerInstruction}.
 */
const LIVENESS_LABELS: Record<FixedLivenessChallenge, string> = {
  TURN_HEAD_LEFT: 'Drehen Sie den Kopf leicht nach links',
  TURN_HEAD_RIGHT: 'Drehen Sie den Kopf leicht nach rechts',
  LOOK_UP: 'Schauen Sie kurz nach oben',
  SHOW_TWO_FINGERS: 'Halten Sie zwei Finger neben Ihr Gesicht',
}

/** Tasks whose wording is the same every time — everything but the fingers. */
type FixedLivenessChallenge = Exclude<KycLivenessChallenge, 'SHOW_FINGERS'>

const SLOT_LABELS: Record<KycSelfSlot, string> = {
  front: 'Vorderseite des Ausweises',
  back: 'Rückseite des Ausweises',
  selfie: 'Selfie',
  pdf: 'PDF-Kopie des Ausweises',
}

const NO_FILES: Record<KycSelfSlot, boolean> = {
  front: false,
  back: false,
  selfie: false,
  pdf: false,
}

/**
 * What is wrong with a picture, as the customer is told (Issue #1165).
 *
 * `ok` never reaches the screen — it is in the map only so every verdict of
 * the API has a sentence here.
 */
const QUALITY_HINTS: Record<KycImageQualityValue, string> = {
  ok: '',
  not_document: 'Wir konnten keinen Ausweis erkennen.',
  glare: 'Auf dem Bild spiegelt es — bitte ohne direkte Lichtquelle neu aufnehmen.',
  blurry: 'Das Bild ist unscharf.',
  cut_off: 'Der Ausweis ist nicht ganz im Bild.',
}

/** HTTP status of an Axios-like error, without depending on the instance. */
function statusOf(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined
  return (err as { response?: { status?: number } }).response?.status
}

/** `detail` of a FastAPI error response, if it is an object. */
function detailOf(err: unknown): Record<string, unknown> | undefined {
  if (typeof err !== 'object' || err === null) return undefined
  const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
  return typeof detail === 'object' && detail !== null
    ? (detail as Record<string, unknown>)
    : undefined
}

function isPdfLike(mime: string, name: string): boolean {
  return mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf')
}

function isPdf(file: File): boolean {
  return isPdfLike(file.type, file.name)
}

export default function KycCaptureWizard({
  api,
  livenessChallenge,
  livenessFingerCount,
  onSubmitted,
  onBack,
}: Props) {
  const { t } = useTranslation()

  const [stage, setStage] = useState<Stage>('setup')
  const [documentType, setDocumentType] = useState<KycDocumentType>(KYC_DOCUMENT_TYPE.ID_CARD)
  const [mode, setMode] = useState<KycCaptureMode>(KYC_CAPTURE_MODE.LIVE)
  const [uploaded, setUploaded] = useState<Record<KycSelfSlot, boolean>>(NO_FILES)
  const [captures, setCaptures] = useState<Partial<Record<KycSelfSlot, StoredCapture>>>({})
  /** Object URLs whose `<img>` the browser refused (e.g. a HEIC upload). */
  const [brokenPreviews, setBrokenPreviews] = useState<string[]>([])
  const [liveIndex, setLiveIndex] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<CaptureError | null>(null)
  const [cameraNotice, setCameraNotice] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  const [issuingCountry, setIssuingCountry] = useState('CH')
  const [documentNumber, setDocumentNumber] = useState('')
  const [expiryDate, setExpiryDate] = useState('')

  // Automatic reading of the document (Issue #1165)
  /** The picture we ask for again, instead of showing the form. */
  const [critique, setCritique] = useState<ImageCritique | null>(null)
  /** Pages already retaken because the analysis asked — they get a way out. */
  const [retakenOnRequest, setRetakenOnRequest] = useState<KycSelfSlot[]>([])
  /** The waiting took long enough to offer filling in by hand. */
  const [waitedTooLong, setWaitedTooLong] = useState(false)
  /** Whether the form shows values the analysis read for us. */
  const [prefilled, setPrefilled] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const slotInputRef = useRef<HTMLInputElement>(null)
  const targetSlotRef = useRef<KycSelfSlot | null>(null)
  /** Mirror of `captures` so the unmount cleanup sees the current URLs. */
  const capturesRef = useRef<Partial<Record<KycSelfSlot, StoredCapture>>>({})
  /**
   * Mirror of `uploaded`, so a series of uploads sees what the one before it
   * stored — the state update only arrives on the next render.
   */
  const uploadedRef = useRef<Record<KycSelfSlot, boolean>>(NO_FILES)
  /** Guards «genau einmal pro Aufnahme-Runde» for the analysis call. */
  const analysisStartedRef = useRef(false)
  /**
   * Last known verdict of the analysis.
   *
   * A ref, not state: nothing on screen hangs off it directly — the waiting
   * station reads it, and what the customer sees comes from `critique`, the
   * stage and the form fields.
   */
  const analysisRef = useRef<KycSelfAnalysis | null>(null)
  /** The running reading request — the waiting station lets it finish first. */
  const analysisCallRef = useRef<Promise<void> | null>(null)

  // Nobody owns these object URLs but us — release them when the wizard closes.
  useEffect(
    () => () => {
      Object.values(capturesRef.current).forEach((capture) => {
        if (capture) URL.revokeObjectURL(capture.url)
      })
      capturesRef.current = {}
    },
    [],
  )

  const needsBackSide = KYC_TWO_SIDED_DOCUMENT_TYPES.includes(documentType)
  const hasPdf = uploaded.pdf

  /** Pictures of the document, in capture order — the PDF replaces them. */
  const imageSlots: KycSelfSlot[] = needsBackSide
    ? [KYC_SELF_SLOT.front, KYC_SELF_SLOT.back]
    : [KYC_SELF_SLOT.front]

  /** Live capture always ends with the selfie — that is the liveness check. */
  const liveSlots: KycSelfSlot[] = [...imageSlots, KYC_SELF_SLOT.selfie]

  /** The pictures the analysis reads — the PDF copy replaces the two sides. */
  const documentSlots: KycSelfSlot[] = hasPdf ? [KYC_SELF_SLOT.pdf] : imageSlots

  /** Whether the document itself is on file; the selfie is not part of it. */
  const documentsOnFile = (files: Record<KycSelfSlot, boolean>): boolean =>
    files.pdf || imageSlots.every((slot) => files[slot])

  const slotLabel = (slot: KycSelfSlot): string =>
    t(`kyc.capture.slots.${slot}`, SLOT_LABELS[slot])

  const challenge =
    livenessChallenge && (KYC_LIVENESS_CHALLENGE_KEYS as readonly string[]).includes(livenessChallenge)
      ? (livenessChallenge as KycLivenessChallenge)
      : null

  /**
   * The number of fingers to show — only for `SHOW_FINGERS`, and only when the
   * server sent a number a hand can actually make (1–5).
   */
  const fingerCount =
    challenge === 'SHOW_FINGERS' &&
    typeof livenessFingerCount === 'number' &&
    Number.isInteger(livenessFingerCount) &&
    livenessFingerCount >= KYC_LIVENESS_FINGER_MIN &&
    livenessFingerCount <= KYC_LIVENESS_FINGER_MAX
      ? livenessFingerCount
      : null

  /**
   * The sentence the person is asked to follow — `null` means we say nothing.
   *
   * A finger task without a usable number falls silent rather than asking for
   * «{{count}} Finger»; a human still checks the selfie afterwards.
   */
  const livenessInstruction: string | null =
    challenge === 'SHOW_FINGERS'
      ? fingerCount === null
        ? null
        : t('kyc.capture.liveness.instructions.SHOW_FINGERS', {
            count: fingerCount,
            defaultValue: `Zeigen Sie ${fingerCount} Finger neben dem Gesicht`,
          })
      : challenge
        ? t(`kyc.capture.liveness.instructions.${challenge}`, LIVENESS_LABELS[challenge])
        : null

  const uploadComplete = hasPdf || imageSlots.every((slot) => uploaded[slot])

  /** The stored captures the person checks before submitting (Issue #1115). */
  const summaryTiles = (hasPdf ? [KYC_SELF_SLOT.pdf] : [...imageSlots, KYC_SELF_SLOT.selfie])
    .map((slot) => ({ slot, capture: captures[slot] }))
    .filter(
      (tile): tile is { slot: KycSelfSlot; capture: StoredCapture } => tile.capture !== undefined,
    )

  // -------------------------------------------------------------------------
  // Automatic reading of the document (Issue #1165)
  // -------------------------------------------------------------------------

  /**
   * Asks the API to read the pictures — exactly once per capture round.
   *
   * Whatever goes wrong here stays with us: the customer fills the three
   * fields in by hand, as before, and never sees an error about it.
   */
  const startAnalysis = () => {
    const analyze = api.analyze
    if (!analyze || analysisStartedRef.current) return
    analysisStartedRef.current = true
    analysisCallRef.current = analyze()
      .then((status) => {
        if (status?.analysis) analysisRef.current = status.analysis
      })
      .catch(() => {
        // Silent on purpose — the waiting station falls through to the form.
      })
  }

  /** Forget the verdict: the pictures changed, so it has to be read again. */
  const resetAnalysis = () => {
    analysisStartedRef.current = false
    analysisRef.current = null
    setCritique(null)
    setWaitedTooLong(false)
  }

  /** The first page the analysis was not happy with, or `null` if all is well. */
  const poorPictureOf = (section: KycSelfAnalysis): ImageCritique | null => {
    if (section.document_detected === false) {
      // No page named: the front carries the data, so that is the one we want.
      return { slot: documentSlots[0], reason: KYC_IMAGE_QUALITY.NOT_DOCUMENT }
    }
    const quality = section.quality ?? {}
    for (const slot of documentSlots) {
      const verdict = quality[slot]
      if (verdict && verdict !== KYC_IMAGE_QUALITY.OK) return { slot, reason: verdict }
    }
    return null
  }

  /** Takes over what the analysis read; only values it actually found. */
  const applyReadValues = (section: KycSelfAnalysis) => {
    const country = section.issuing_country?.trim().slice(0, 2).toUpperCase()
    let found = false
    if (country) {
      setIssuingCountry(country)
      found = true
    }
    if (section.document_number?.trim()) {
      setDocumentNumber(section.document_number.trim())
      found = true
    }
    if (section.expiry_date) {
      setExpiryDate(section.expiry_date)
      found = true
    }
    if (found) setPrefilled(true)
  }

  /** After the last picture: wait for the reading, or straight to the form. */
  const goAfterCapture = () => {
    setStage(api.analyze ? 'analyzing' : 'details')
  }

  /**
   * Station «Ihr Ausweis wird geprüft …» (Issue #1165).
   *
   * `done` decides between the picture critique and the filled-in form.
   * `pending` keeps us here, asking every few seconds. `failed`, `disabled`,
   * `none` — and a status that carries no `analysis` at all — open the form as
   * before, without a word about it. After half a minute the way out appears,
   * after three minutes the form opens by itself: nobody waits forever.
   */
  useEffect(() => {
    if (stage !== 'analyzing' || critique) return
    let cancelled = false
    let nextPoll: ReturnType<typeof setTimeout> | undefined

    /** `true` when the waiting is over — the caller stops asking. */
    const settle = (section: KycSelfAnalysis | null): boolean => {
      if (section?.status === KYC_ANALYSIS_STATUS.PENDING) return false
      if (section?.status === KYC_ANALYSIS_STATUS.DONE) {
        const poor = poorPictureOf(section)
        if (poor) setCritique(poor)
        else {
          applyReadValues(section)
          setStage('details')
        }
        return true
      }
      setStage('details')
      return true
    }

    const ask = async () => {
      try {
        const status = await api.status()
        if (cancelled) return
        const section = status?.analysis ?? null
        analysisRef.current = section
        if (settle(section)) return
      } catch {
        // One failed poll says nothing — the next one may well answer.
      }
      if (cancelled) return
      nextPoll = setTimeout(() => void ask(), KYC_ANALYSIS_POLL_INTERVAL_MS)
    }

    const wayOut = setTimeout(() => setWaitedTooLong(true), KYC_ANALYSIS_SKIP_AFTER_MS)
    const ceiling = setTimeout(() => setStage('details'), KYC_ANALYSIS_TIMEOUT_MS)

    void (async () => {
      // The reading request may still be on its way: its answer counts, and a
      // status asked before it would wrongly say «nothing to wait for».
      await analysisCallRef.current
      if (cancelled) return
      // Already read while the selfie was taken — then there is no waiting.
      if (analysisRef.current && settle(analysisRef.current)) return
      await ask()
    })()

    return () => {
      cancelled = true
      if (nextPoll) clearTimeout(nextPoll)
      clearTimeout(wayOut)
      clearTimeout(ceiling)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, critique])

  // -------------------------------------------------------------------------
  // Uploading
  // -------------------------------------------------------------------------

  const uploadErrorFor = (err: unknown): CaptureError => {
    switch (statusOf(err)) {
      case 413:
        return {
          text: t(
            'kyc.capture.errors.tooLarge',
            'Die Datei ist zu gross. Erlaubt sind Dateien bis 15 MB.',
          ),
        }
      case 415:
        return {
          text: t(
            'kyc.capture.errors.wrongType',
            'Dieses Format können wir nicht lesen. Erlaubt sind JPG, PNG, HEIC und PDF.',
          ),
        }
      case 409:
        return {
          text: t(
            'kyc.capture.errors.alreadySubmitted',
            'Diese Identifikation wurde bereits eingereicht und wird geprüft.',
          ),
        }
      default:
        return {
          text: t(
            'kyc.capture.errors.uploadFailed',
            'Die Datei konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.',
          ),
        }
    }
  }

  const store = async (
    slot: KycSelfSlot,
    file: Blob,
    filename: string,
    captureMode: KycCaptureMode,
  ): Promise<boolean> => {
    setBusy(true)
    setError(null)
    try {
      const status = await api.upload(slot, file, filename, captureMode)
      const merged = { ...uploadedRef.current, ...(status?.files ?? {}), [slot]: true }
      uploadedRef.current = merged
      setUploaded(merged)
      // The document is complete: read it now, so the reading runs while the
      // selfie is still being taken (Issue #1165).
      if (documentsOnFile(merged)) void startAnalysis()
      // Keep exactly the blob that went up, so the summary shows the stored
      // version. A replaced slot releases its predecessor right away.
      const replaced = capturesRef.current[slot]
      if (replaced) URL.revokeObjectURL(replaced.url)
      capturesRef.current = {
        ...capturesRef.current,
        [slot]: { url: URL.createObjectURL(file), mime: file.type, name: filename },
      }
      setCaptures(capturesRef.current)
      return true
    } catch (err) {
      setError(uploadErrorFor(err))
      return false
    } finally {
      setBusy(false)
    }
  }

  // -------------------------------------------------------------------------
  // Camera
  // -------------------------------------------------------------------------

  const handleLiveCapture = async (file: Blob, filename: string) => {
    const slot = liveSlots[liveIndex]
    if (!slot) return
    const stored = await store(slot, file, filename, KYC_CAPTURE_MODE.LIVE)
    if (!stored) return
    // Next picture we still need. A retake from the summary (Issue #1115) leaves
    // nothing open, so it goes straight back to the summary.
    const next = liveSlots.findIndex(
      (candidate, index) => index > liveIndex && !uploaded[candidate],
    )
    if (next >= 0) setLiveIndex(next)
    else goAfterCapture()
  }

  /** No camera, no permission: say so and offer the upload right away. */
  const handleCameraUnavailable = (reason: CameraUnavailableReason) => {
    setCameraNotice(
      reason === 'denied'
        ? t(
            'kyc.capture.camera.deniedFallback',
            'Wir dürfen nicht auf Ihre Kamera zugreifen. Laden Sie die Aufnahmen stattdessen als Datei hoch.',
          )
        : t(
            'kyc.capture.camera.unavailableFallback',
            'Wir finden keine Kamera auf diesem Gerät. Laden Sie die Aufnahmen stattdessen als Datei hoch.',
          ),
    )
    setMode(KYC_CAPTURE_MODE.UPLOAD)
  }

  // -------------------------------------------------------------------------
  // File selection
  // -------------------------------------------------------------------------

  /** Rejected in the browser already — the API would answer 413/415. */
  const rejectReason = (file: File): CaptureError | null => {
    if (file.size > KYC_UPLOAD_MAX_BYTES) {
      return {
        text: t(
          'kyc.capture.errors.tooLarge',
          'Die Datei ist zu gross. Erlaubt sind Dateien bis 15 MB.',
        ),
      }
    }
    const type = (file.type || '').toLowerCase()
    const name = (file.name || '').toLowerCase()
    const accepted = type
      ? KYC_UPLOAD_MIME_TYPES.includes(type)
      : KYC_UPLOAD_EXTENSIONS.some((extension) => name.endsWith(extension))
    if (!accepted) {
      return {
        text: t(
          'kyc.capture.errors.wrongType',
          'Dieses Format können wir nicht lesen. Erlaubt sind JPG, PNG, HEIC und PDF.',
        ),
      }
    }
    return null
  }

  const handleFiles = async (files: File[], target?: KycSelfSlot) => {
    // Track the assignment locally — the state updates arrive asynchronously
    const filled = { ...uploaded }
    for (const file of files) {
      const rejected = rejectReason(file)
      if (rejected) {
        setError(rejected)
        return
      }
      // A PDF always goes into its own slot, an image never does — whatever
      // row the file was picked on.
      let slot: KycSelfSlot | undefined
      if (isPdf(file)) slot = KYC_SELF_SLOT.pdf
      else if (target && target !== KYC_SELF_SLOT.pdf) slot = target
      else slot = [...imageSlots, KYC_SELF_SLOT.selfie].find((candidate) => !filled[candidate])
      if (!slot) {
        setError({
          text: t(
            'kyc.capture.errors.allSlotsFilled',
            'Alle Seiten sind bereits erfasst. Nutzen Sie «Ersetzen», um eine Aufnahme auszutauschen.',
          ),
        })
        return
      }
      const stored = await store(slot, file, file.name || slot, KYC_CAPTURE_MODE.UPLOAD)
      if (!stored) return
      filled[slot] = true
    }
  }

  const openPicker = (slot?: KycSelfSlot) => {
    targetSlotRef.current = slot ?? null
    if (slot) slotInputRef.current?.click()
    else fileInputRef.current?.click()
  }

  // -------------------------------------------------------------------------
  // Submitting
  // -------------------------------------------------------------------------

  /** The slots the API is missing (422 `KYC_INCOMPLETE`). */
  const missingSlotsOf = (err: unknown): KycSelfSlot[] => {
    const detail = detailOf(err)
    if (detail?.code !== 'KYC_INCOMPLETE' || !Array.isArray(detail.missing)) return []
    return (detail.missing as unknown[]).filter(
      (slot): slot is KycSelfSlot => typeof slot === 'string' && slot in SLOT_LABELS,
    )
  }

  const submitErrorFor = (err: unknown): CaptureError => {
    const detail = detailOf(err)
    const code = typeof detail?.code === 'string' ? detail.code : undefined

    if (code === 'KYC_INCOMPLETE') {
      return {
        text: t('kyc.capture.errors.incomplete', 'Es fehlen noch Aufnahmen:'),
        items: missingSlotsOf(err).map(slotLabel),
      }
    }
    if (code === 'KYC_DOCUMENT_EXPIRED') {
      return {
        text: t(
          'kyc.capture.errors.expired',
          'Dieser Ausweis ist abgelaufen. Bitte verwenden Sie einen gültigen Ausweis.',
        ),
      }
    }
    if (statusOf(err) === 409) {
      return {
        text: t(
          'kyc.capture.errors.alreadySubmitted',
          'Diese Identifikation wurde bereits eingereicht und wird geprüft.',
        ),
      }
    }
    if (statusOf(err) === 413 || statusOf(err) === 415) return uploadErrorFor(err)
    return {
      text: t(
        'kyc.capture.errors.submitFailed',
        'Das Einreichen hat nicht geklappt. Bitte versuchen Sie es erneut.',
      ),
    }
  }

  const countryValid = /^[A-Za-z]{2}$/.test(issuingCountry.trim())

  const handleSubmit = async () => {
    if (!countryValid || busy) return
    setBusy(true)
    setError(null)
    try {
      const status = await api.submit({
        document_type: documentType,
        issuing_country: issuingCountry.trim().toUpperCase(),
        document_number: documentNumber.trim() || null,
        expiry_date: expiryDate || null,
      })
      onSubmitted(status)
    } catch (err) {
      setError(submitErrorFor(err))
      // Missing pages: back to the capture, at the first one that is missing —
      // the message alone would leave the user searching.
      const missing = missingSlotsOf(err)
      if (missing.length > 0) {
        const reopened = {
          ...uploadedRef.current,
          ...Object.fromEntries(missing.map((slot) => [slot, false])),
        }
        uploadedRef.current = reopened
        setUploaded(reopened)
        const nextLive = liveSlots.findIndex((slot) => missing.includes(slot))
        if (mode === KYC_CAPTURE_MODE.LIVE && nextLive >= 0) setLiveIndex(nextLive)
        setStage('capture')
      }
    } finally {
      setBusy(false)
    }
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  /** Back to the capture, at the slot the person wants to do again. */
  const retake = (slot: KycSelfSlot) => {
    if (mode === KYC_CAPTURE_MODE.LIVE) {
      const index = liveSlots.indexOf(slot)
      if (index >= 0) setLiveIndex(index)
    }
    // A new picture of the document has to be read again (Issue #1165).
    if (slot !== KYC_SELF_SLOT.selfie) resetAnalysis()
    setError(null)
    setStage('capture')
  }

  /** «Neu aufnehmen» of the picture critique — straight at that very page. */
  const retakeForCritique = (slot: KycSelfSlot) => {
    setRetakenOnRequest((previous) => (previous.includes(slot) ? previous : [...previous, slot]))
    // The page counts as open again: the capture asks for it, and the reading
    // starts over as soon as the new picture is stored.
    const reopened = { ...uploadedRef.current, [slot]: false }
    uploadedRef.current = reopened
    setUploaded(reopened)
    retake(slot)
  }

  /** The way out of a repeated critique — take what we read and move on. */
  const continueDespiteCritique = () => {
    const section = analysisRef.current
    if (section) applyReadValues(section)
    setCritique(null)
    setStage('details')
  }

  const summaryBox = summaryTiles.length > 0 && (
    <div data-testid="kyc-capture-summary" className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700">
        {t('kyc.capture.summary.title', 'Ihre Aufnahmen')}
      </h4>
      <p className="text-xs text-gray-500">
        {t(
          'kyc.capture.summary.lead',
          'So haben wir Ihre Aufnahmen gespeichert. Ist etwas unscharf oder abgeschnitten, nehmen Sie es neu auf.',
        )}
      </p>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {summaryTiles.map(({ slot, capture }) => (
          <li
            key={slot}
            data-testid={`kyc-capture-summary-${slot}`}
            className="p-2 rounded-lg border border-gray-200 bg-gray-50"
          >
            <p className="text-xs font-medium text-gray-600 truncate">{slotLabel(slot)}</p>
            <div className="mt-1.5 flex h-40 items-center justify-center overflow-hidden rounded bg-white">
              {isPdfLike(capture.mime, capture.name) ? (
                <div className="p-2 text-center">
                  <FileUp size={20} className="mx-auto text-gray-400" />
                  <p className="mt-1 text-xs text-gray-700 break-all">{capture.name}</p>
                  <p className="text-xs text-gray-400">
                    {t('kyc.capture.summary.pdfNote', 'PDF-Kopie')}
                  </p>
                </div>
              ) : brokenPreviews.includes(capture.url) ? (
                <p className="p-2 text-center text-xs text-gray-500">
                  {t(
                    'kyc.capture.summary.previewUnavailable',
                    'Vorschau nicht verfügbar — die Datei ist gespeichert.',
                  )}
                </p>
              ) : (
                <img
                  src={capture.url}
                  alt={slotLabel(slot)}
                  onError={() =>
                    setBrokenPreviews((previous) =>
                      previous.includes(capture.url) ? previous : [...previous, capture.url],
                    )
                  }
                  className="max-h-40 w-full object-contain"
                />
              )}
            </div>
            <button
              data-testid={`kyc-capture-summary-retake-${slot}`}
              type="button"
              onClick={() => retake(slot)}
              disabled={busy}
              className="mt-1.5 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
            >
              {t('kyc.capture.summary.retake', 'Neu aufnehmen')}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )

  const errorBox = error && (
    <div
      data-testid="kyc-capture-error"
      role="alert"
      className="shrink-0 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
    >
      <p>{error.text}</p>
      {error.items && error.items.length > 0 && (
        <ul className="list-disc list-inside mt-1">
          {error.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  )

  const backLink = (onClick: () => void) => (
    <button
      data-testid="kyc-capture-back-btn"
      type="button"
      onClick={onClick}
      className="flex shrink-0 items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
    >
      <ChevronLeft size={16} />
      {t('common.back', 'Zurück')}
    </button>
  )

  /**
   * The live capture is one column over the whole height the host gives us
   * (Issue #1143) — chips, task box, hints, button and «Zurück» keep their
   * size, the picture takes what is left. The other stations flow as before.
   */
  const liveCapture = stage === 'capture' && mode === KYC_CAPTURE_MODE.LIVE

  return (
    <div
      data-testid="kyc-capture-wizard"
      className={liveCapture ? 'flex min-h-0 flex-auto flex-col' : 'space-y-4'}
    >
      {/* Station 1 — what do you have, and how do you want to hand it in? */}
      {stage === 'setup' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {t('kyc.capture.setup.title', 'Ausweis erfassen')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t(
                'kyc.capture.setup.lead',
                'Wir brauchen ein Bild Ihres Ausweises und ein Selfie. Sie können beides direkt aufnehmen oder als Datei hochladen.',
              )}
            </p>
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">
              {t('kyc.capture.setup.documentType', 'Welchen Ausweis legen Sie vor?')}
            </legend>
            <div className="space-y-2">
              {KYC_DOCUMENT_TYPES.map((type) => (
                <label
                  key={type}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    documentType === type
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    data-testid={`kyc-capture-doctype-${type}`}
                    type="radio"
                    name="kyc-capture-document-type"
                    value={type}
                    checked={documentType === type}
                    onChange={() => setDocumentType(type)}
                    className="mt-0.5 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">
                    {t(`kyc.capture.documentTypes.${type}`, DOCUMENT_TYPE_LABELS[type])}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              data-testid="kyc-capture-mode-live-btn"
              type="button"
              onClick={() => {
                setMode(KYC_CAPTURE_MODE.LIVE)
                setLiveIndex(0)
                setStage('capture')
              }}
              className="text-left p-4 rounded-xl border-2 border-primary-500 bg-primary-50 hover:bg-primary-100 transition-colors"
            >
              <ScanFace size={20} className="text-primary-600" />
              <p className="font-semibold text-primary-700 mt-2">
                {t('kyc.capture.setup.live', 'Mit Kamera aufnehmen')}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">
                {t(
                  'kyc.capture.setup.liveDesc',
                  'Handy-Kamera oder Webcam — Ausweis und Selfie in zwei Minuten.',
                )}
              </p>
            </button>
            <button
              data-testid="kyc-capture-mode-upload-btn"
              type="button"
              onClick={() => {
                setMode(KYC_CAPTURE_MODE.UPLOAD)
                setStage('capture')
              }}
              className="text-left p-4 rounded-xl border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Upload size={20} className="text-gray-500" />
              <p className="font-semibold text-gray-900 mt-2">
                {t('kyc.capture.setup.upload', 'Dateien hochladen')}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">
                {t(
                  'kyc.capture.setup.uploadDesc',
                  'Fotos (JPG, PNG, HEIC) oder eine PDF-Kopie Ihres Ausweises.',
                )}
              </p>
            </button>
          </div>

          {onBack && backLink(onBack)}
        </div>
      )}

      {/* Station 2 — capture with the camera */}
      {liveCapture && (
        <div className="flex min-h-0 flex-auto flex-col gap-3">
          <ol
            data-testid="kyc-capture-live-steps"
            className="flex shrink-0 flex-wrap items-center gap-2 text-xs sm:text-sm"
          >
            {liveSlots.map((slot, index) => (
              <li
                key={slot}
                aria-current={index === liveIndex ? 'step' : undefined}
                className={`px-2.5 py-1 rounded-full ${
                  index === liveIndex
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : uploaded[slot]
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-500'
                }`}
              >
                {slotLabel(slot)}
              </li>
            ))}
          </ol>

          {/* The task for the live selfie, large and before the picture */}
          {liveSlots[liveIndex] === KYC_SELF_SLOT.selfie && livenessInstruction && (
            <div
              data-testid="kyc-capture-liveness"
              className="shrink-0 p-3 rounded-lg bg-primary-50 border border-primary-200"
            >
              <p className="text-xs uppercase tracking-wide text-primary-700">
                {t('kyc.capture.liveness.title', 'Ihre Aufgabe für das Selfie')}
              </p>
              {fingerCount !== null && (
                <p
                  data-testid="kyc-capture-liveness-count"
                  aria-hidden="true"
                  className="text-3xl font-bold text-primary-800 mt-1 leading-none"
                >
                  {fingerCount}
                </p>
              )}
              <p className="text-base sm:text-lg font-semibold text-primary-800 mt-1">
                {livenessInstruction}
              </p>
            </div>
          )}

          <CameraCapture
            key={liveSlots[liveIndex]}
            slot={liveSlots[liveIndex]}
            facingMode={liveSlots[liveIndex] === KYC_SELF_SLOT.selfie ? 'user' : 'environment'}
            label={slotLabel(liveSlots[liveIndex])}
            hint={
              liveSlots[liveIndex] === KYC_SELF_SLOT.selfie
                ? // The finger task belongs over the live picture — that is where
                  // the person looks while taking the selfie.
                  (fingerCount !== null && livenessInstruction
                    ? livenessInstruction
                    : t(
                        'kyc.capture.camera.selfieHint',
                        'Ihr Gesicht gut sichtbar, ohne Kopfbedeckung.',
                      ))
                : t('kyc.capture.camera.documentHint', 'Alle vier Ecken im Bild, keine Spiegelungen.')
            }
            // The camera needs the number itself, not only as a sentence: with a
            // finger task it counts down before releasing (Issue #1144).
            livenessFingerCount={
              liveSlots[liveIndex] === KYC_SELF_SLOT.selfie ? fingerCount : null
            }
            onCapture={handleLiveCapture}
            onUnavailable={handleCameraUnavailable}
            busy={busy}
          />

          {errorBox}
          {backLink(() => setStage('setup'))}
        </div>
      )}

      {/* Station 2b — hand in files instead */}
      {stage === 'capture' && mode === KYC_CAPTURE_MODE.UPLOAD && (
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {t('kyc.capture.upload.title', 'Dateien hochladen')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t(
                'kyc.capture.upload.lead',
                'Fotos von Vorder- und Rückseite — oder eine PDF-Kopie des Ausweises. Ein Selfie hilft uns bei der Prüfung, ist hier aber freiwillig.',
              )}
            </p>
          </div>

          {cameraNotice && (
            <p
              data-testid="kyc-capture-camera-notice"
              role="status"
              className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
            >
              {cameraNotice}
            </p>
          )}

          <div
            data-testid="kyc-capture-dropzone"
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              void handleFiles(Array.from(e.dataTransfer?.files ?? []))
            }}
            className={`rounded-xl border-2 border-dashed p-5 text-center transition-colors ${
              dragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 bg-gray-50'
            }`}
          >
            <FileUp size={22} className="mx-auto text-gray-400" />
            <p className="text-sm text-gray-600 mt-2">
              {t('kyc.capture.upload.dropHint', 'Dateien hierher ziehen oder auswählen')}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {t('kyc.capture.upload.formats', 'JPG, PNG, HEIC oder PDF — bis 15 MB pro Datei.')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-2 mt-3">
              <button
                data-testid="kyc-capture-choose-btn"
                type="button"
                onClick={() => openPicker()}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {t('kyc.capture.upload.choose', 'Dateien auswählen')}
              </button>
              {/* Phone fallback: opens the camera app directly */}
              <label className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer sm:hidden">
                {t('kyc.capture.upload.nativeCamera', 'Mit der Kamera aufnehmen')}
                <input
                  data-testid="kyc-capture-native-camera-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    void handleFiles(Array.from(e.target.files ?? []))
                    e.target.value = ''
                  }}
                />
              </label>
            </div>
          </div>

          <ul className="space-y-2">
            {(hasPdf ? [KYC_SELF_SLOT.pdf] : [...imageSlots, KYC_SELF_SLOT.selfie]).map((slot) => (
              <li
                key={slot}
                data-testid={`kyc-capture-slot-${slot}`}
                data-state={uploaded[slot] ? 'filled' : 'empty'}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200"
              >
                <span className="flex items-center gap-2 min-w-0 text-sm text-gray-700">
                  {uploaded[slot] ? (
                    <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                  ) : (
                    <Circle size={16} className="text-gray-300 flex-shrink-0" />
                  )}
                  <span className="truncate">
                    {slotLabel(slot)}
                    {slot === KYC_SELF_SLOT.selfie && (
                      <span className="text-gray-400"> ({t('common.optional', 'optional')})</span>
                    )}
                  </span>
                </span>
                <button
                  data-testid={`kyc-capture-slot-choose-${slot}`}
                  type="button"
                  onClick={() => openPicker(slot)}
                  disabled={busy}
                  className="text-sm text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50 flex-shrink-0"
                >
                  {uploaded[slot]
                    ? t('kyc.capture.upload.replace', 'Ersetzen')
                    : t('kyc.capture.upload.select', 'Datei wählen')}
                </button>
              </li>
            ))}
          </ul>

          {!hasPdf && (
            <p className="text-xs text-gray-500">
              {t(
                'kyc.capture.upload.pdfHint',
                'Sie haben eine PDF-Kopie Ihres Ausweises? Laden Sie einfach das PDF hoch — Vorder- und Rückseite entfallen dann.',
              )}
            </p>
          )}

          {errorBox}

          <div className="flex items-center justify-between gap-3">
            {backLink(() => setStage('setup'))}
            <button
              data-testid="kyc-capture-to-details-btn"
              type="button"
              onClick={goAfterCapture}
              disabled={busy || !uploadComplete}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {t('common.next', 'Weiter')}
            </button>
          </div>
        </div>
      )}

      {/* Station 2c — our AI reads the document while the person waits */}
      {stage === 'analyzing' && !critique && (
        <div
          data-testid="kyc-capture-analyzing"
          role="status"
          aria-live="polite"
          className="flex flex-col items-center gap-3 py-10 text-center"
        >
          <Loader2 size={32} className="animate-spin text-primary-600" aria-hidden="true" />
          <p className="text-base font-semibold text-gray-900">
            {t('kyc.capture.analysis.waitTitle', 'Ihr Ausweis wird geprüft …')}
          </p>
          <p className="text-sm text-gray-500">
            {t('kyc.capture.analysis.waitHint', 'Das kann einen Moment dauern.')}
          </p>
          {waitedTooLong && (
            <button
              data-testid="kyc-capture-analysis-skip-btn"
              type="button"
              onClick={() => setStage('details')}
              className="mt-2 text-sm font-medium text-primary-600 underline hover:text-primary-800"
            >
              {t('kyc.capture.analysis.skip', 'Ohne Prüfung weiter und selbst ausfüllen')}
            </button>
          )}
        </div>
      )}

      {/* Station 2d — the picture is unusable, we ask for a new one */}
      {stage === 'analyzing' && critique && (
        <div data-testid="kyc-capture-critique" className="space-y-4">
          <div
            role="status"
            className="flex gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
          >
            <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-semibold">
                {t('kyc.capture.analysis.critiqueTitle', {
                  slot: slotLabel(critique.slot),
                  defaultValue: `Bitte nehmen Sie die ${slotLabel(critique.slot)} neu auf.`,
                })}
              </p>
              <p data-testid="kyc-capture-critique-reason">
                {t(
                  `kyc.capture.analysis.quality.${critique.reason}`,
                  QUALITY_HINTS[critique.reason],
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              data-testid="kyc-capture-critique-retake-btn"
              type="button"
              onClick={() => retakeForCritique(critique.slot)}
              className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
            >
              {t('kyc.capture.analysis.retake', 'Neu aufnehmen')}
            </button>
            {/* Asked for the same page twice: nobody is kept here (Issue #1165) */}
            {retakenOnRequest.includes(critique.slot) && (
              <button
                data-testid="kyc-capture-critique-continue-btn"
                type="button"
                onClick={continueDespiteCritique}
                className="text-sm font-medium text-gray-600 underline hover:text-gray-900"
              >
                {t('kyc.capture.analysis.continueAnyway', 'Trotzdem weiter')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Station 3 — the few details we may not read from the picture */}
      {stage === 'details' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {t('kyc.capture.details.title', 'Angaben zum Ausweis')}
            </h3>
            <p data-testid="kyc-capture-details-lead" className="text-sm text-gray-500 mt-1">
              {prefilled
                ? t(
                    'kyc.capture.analysis.prefilled',
                    'Wir haben Ihren Ausweis ausgelesen — bitte kurz prüfen.',
                  )
                : t(
                    'kyc.capture.details.lead',
                    'Nur noch das Ausstellungsland — Nummer und Ablaufdatum sind freiwillig.',
                  )}
            </p>
          </div>

          {summaryBox}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="kyc-capture-country"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('kyc.capture.details.country', 'Ausstellungsland')}
              </label>
              <input
                data-testid="kyc-capture-country-input"
                id="kyc-capture-country"
                type="text"
                value={issuingCountry}
                onChange={(e) => setIssuingCountry(e.target.value.toUpperCase())}
                maxLength={2}
                placeholder="CH"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                {t('kyc.capture.details.countryHint', 'Zwei Buchstaben, z. B. CH, DE oder FR.')}
              </p>
            </div>
            <div>
              <label
                htmlFor="kyc-capture-number"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('kyc.capture.details.number', 'Ausweisnummer')}
                <span className="text-gray-400 font-normal"> ({t('common.optional', 'optional')})</span>
              </label>
              <input
                data-testid="kyc-capture-number-input"
                id="kyc-capture-number"
                type="text"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                maxLength={50}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label
                htmlFor="kyc-capture-expiry"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('kyc.capture.details.expiry', 'Gültig bis')}
                <span className="text-gray-400 font-normal"> ({t('common.optional', 'optional')})</span>
              </label>
              <input
                data-testid="kyc-capture-expiry-input"
                id="kyc-capture-expiry"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {errorBox}

          <div className="flex items-center justify-between gap-3">
            {backLink(() => setStage('capture'))}
            <button
              data-testid="kyc-capture-submit-btn"
              type="button"
              onClick={handleSubmit}
              disabled={busy || !countryValid}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {t('kyc.capture.details.submit', 'Zur Prüfung einreichen')}
            </button>
          </div>
        </div>
      )}

      {/* Hidden pickers: free assignment and one targeted at a single slot */}
      <input
        data-testid="kyc-capture-file-input"
        ref={fileInputRef}
        type="file"
        accept={KYC_UPLOAD_ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          void handleFiles(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />
      <input
        data-testid="kyc-capture-slot-input"
        ref={slotInputRef}
        type="file"
        accept={KYC_UPLOAD_ACCEPT}
        className="hidden"
        onChange={(e) => {
          void handleFiles(Array.from(e.target.files ?? []), targetSlotRef.current ?? undefined)
          e.target.value = ''
        }}
      />
    </div>
  )
}
